import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "input",
    "preview",
    "previewImage",
    "progress",
    "progressRing",
    "removeButton",
    "removeField",
    "error",
  ];
  static values = {
    maxSize: { type: Number, default: 5242880 },
    accept: { type: String, default: "image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" },
    sizeClasses: { type: String, default: "w-16 h-16 text-xl" },
    initials: { type: String, default: "?" },
    errorFileSize: { type: String, default: "" },
    errorContentType: { type: String, default: "" },
    uploadingText: { type: String, default: "Uploading..." },
  };

  connect() {
    this.form = this.element.closest("form");
    if (!this.form) return;

    this._onDirectUploadStart = this._handleDirectUploadStart.bind(this);
    this._onDirectUploadProgress = this._handleDirectUploadProgress.bind(this);
    this._onDirectUploadError = this._handleDirectUploadError.bind(this);
    this._onDirectUploadEnd = this._handleDirectUploadEnd.bind(this);

    this.form.addEventListener("direct-upload:start", this._onDirectUploadStart);
    this.form.addEventListener("direct-upload:progress", this._onDirectUploadProgress);
    this.form.addEventListener("direct-upload:error", this._onDirectUploadError);
    this.form.addEventListener("direct-upload:end", this._onDirectUploadEnd);
  }

  disconnect() {
    if (!this.form) return;

    this.form.removeEventListener("direct-upload:start", this._onDirectUploadStart);
    this.form.removeEventListener("direct-upload:progress", this._onDirectUploadProgress);
    this.form.removeEventListener("direct-upload:error", this._onDirectUploadError);
    this.form.removeEventListener("direct-upload:end", this._onDirectUploadEnd);
  }

  openFilePicker(event) {
    event.preventDefault();
    this.inputTarget.click();
  }

  handleFile() {
    this.clearError();
    const file = this.inputTarget.files[0];
    if (!file) return;

    if (!this.validateFile(file)) return;

    this.showPreview(file);
    this.removeFieldTarget.value = "0";
  }

  validateFile(file) {
    if (this.maxSizeValue > 0 && file.size > this.maxSizeValue) {
      this.showError(this.errorFileSizeValue);
      this.inputTarget.value = "";
      return false;
    }

    const accepted = this.acceptValue.split(",").map((t) => t.trim());
    const acceptedMimes = accepted.filter((t) => t.includes("/"));
    const acceptedExts = accepted.filter((t) => t.startsWith("."));
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!acceptedMimes.includes(file.type) && !acceptedExts.includes(ext)) {
      this.showError(this.errorContentTypeValue);
      this.inputTarget.value = "";
      return false;
    }

    return true;
  }

  showPreview(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const size = this.getAvatarSizeClasses();
      this.previewImageTarget.innerHTML = `
        <div class="${size} rounded-ui-full overflow-hidden">
          <img src="${e.target.result}" alt="Preview" class="w-full h-full object-cover">
        </div>
      `;
      this.previewImageTarget.classList.remove("hidden");
      this.previewTarget.classList.add("hidden");
    };
    reader.readAsDataURL(file);
  }

  remove(event) {
    event.preventDefault();
    this.inputTarget.value = "";
    this.removeFieldTarget.value = "1";

    // Reset to initials avatar
    this.previewImageTarget.classList.add("hidden");
    this.previewImageTarget.innerHTML = "";
    this.previewTarget.classList.remove("hidden");

    // Remove the image from the original avatar component, show initials
    const img = this.previewTarget.querySelector("img");
    if (img) {
      img.remove();
    }

    let span = this.previewTarget.querySelector("span");
    if (!span) {
      const container = this.previewTarget.querySelector("div");
      if (container) {
        span = document.createElement("span");
        span.textContent = this.initialsValue;
        container.appendChild(span);
      }
    }
    if (span) {
      span.classList.remove("hidden");
    }

    if (this.hasRemoveButtonTarget) {
      this.removeButtonTarget.classList.add("hidden");
    }
  }

  getAvatarSizeClasses() {
    return this.sizeClassesValue;
  }

  showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
  }

  clearError() {
    this.errorTarget.textContent = "";
    this.errorTarget.classList.add("hidden");
  }

  // Direct Upload event handlers

  _handleDirectUploadStart() {
    this.progressTarget.classList.remove("hidden");
    this._setSubmitButtonsDisabled(true);
  }

  _handleDirectUploadProgress(event) {
    const { progress } = event.detail;
    if (this.hasProgressRingTarget) {
      // Use conic-gradient to show actual progress instead of spinner
      this.progressRingTarget.classList.remove("animate-spin", "border-t-transparent");
      this.progressRingTarget.style.background =
        `conic-gradient(var(--color-brand-500) ${progress}%, transparent ${progress}%)`;
      this.progressRingTarget.style.mask =
        "radial-gradient(farthest-side, transparent calc(100% - 4px), #fff calc(100% - 4px))";
      this.progressRingTarget.style.webkitMask =
        "radial-gradient(farthest-side, transparent calc(100% - 4px), #fff calc(100% - 4px))";
    }
  }

  _handleDirectUploadError(event) {
    event.preventDefault();
    this.progressTarget.classList.add("hidden");
    this._resetProgressRing();
    this.showError(event.detail.error);
    this._setSubmitButtonsDisabled(false);
  }

  _handleDirectUploadEnd() {
    this.progressTarget.classList.add("hidden");
    this._resetProgressRing();
    this._setSubmitButtonsDisabled(false);
  }

  _resetProgressRing() {
    if (this.hasProgressRingTarget) {
      this.progressRingTarget.style.background = "";
      this.progressRingTarget.style.mask = "";
      this.progressRingTarget.style.webkitMask = "";
      this.progressRingTarget.classList.add("animate-spin", "border-t-transparent");
    }
  }

  _setSubmitButtonsDisabled(disabled) {
    if (!this.form) return;
    this.form.querySelectorAll('[type="submit"]').forEach((btn) => {
      btn.disabled = disabled;
    });
  }
}
